import { OutreachMetadata, OutreachTriggerTypes } from '../../types';
import { OutreachTypes } from '../../../../urls';
import { FormikSubscriberOutreachClearMessageValue } from './controls/OutreachConfig/subscriber/FormikSubscriberOutreachClearMessageField';
import { FieldInputProps } from 'formik';
import { FormikSubscriberOutreachContentValue } from './controls/OutreachConfig/subscriber/FormikSubscriberOutreachContentField';

export enum FrequencyUnits {
  Once = 'once',
  Hour = 'hour',
  Hr = 'hr',
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

export const possibleFrequencyUnits: Array<{ id: string; labelId: string }> = [
  {
    id: FrequencyUnits.Hour,
    labelId: 'outreach.form.common.frequencyUnits.hour',
  },
  {
    id: FrequencyUnits.Hr,
    labelId: 'outreach.form.common.frequencyUnits.hour',
  },
  {
    id: FrequencyUnits.Day,
    labelId: 'outreach.form.common.frequencyUnits.day',
  },
  {
    id: FrequencyUnits.Week,
    labelId: 'outreach.form.common.frequencyUnits.week',
  },
  {
    id: FrequencyUnits.Month,
    labelId: 'outreach.form.common.frequencyUnits.month',
  },
];

const mapOutreachTriggerTypeToMetadata = {
  [OutreachTriggerTypes.MaxIncreaseRate]: 'maxIncreaseRateOverADay',
  [OutreachTriggerTypes.MaxPercentage]: 'maxPercentage',
  [OutreachTriggerTypes.MaxValue]: 'maxValue',
  [OutreachTriggerTypes.CSPOutageDetected]: 'cspOutage',
};

export const getMaxFrequencyOfSupportOutreach = (
  metadata: OutreachMetadata,
) => {
  const supportMetadata = metadata[OutreachTypes.Email];
  return supportMetadata.maxFrequency;
};

export const isTriggerTypeAvailable = (
  triggerType: OutreachTriggerTypes,
  metadata: OutreachMetadata,
  outreachType: OutreachTypes,
) => {
  if (triggerType === OutreachTriggerTypes.Time) {
    return true;
  }
  const key = mapOutreachTriggerTypeToMetadata[triggerType];
  const supportMetadata = metadata[outreachType];
  return Boolean((supportMetadata as any)[key]);
};

const SUBJECT_AND_MESSAGE_MAX_LENGTH = 235;
const ALLOWED_FIELDS_REMAINING_CHARACTERS = ['subject', 'message'] as const;
type AllowedFieldsForRemainingCharacters = typeof ALLOWED_FIELDS_REMAINING_CHARACTERS[number];

export function getRemainingCharactersFactory<
  F extends
    | FormikSubscriberOutreachContentValue
    | FormikSubscriberOutreachClearMessageValue
>(
  field: FieldInputProps<F>,
): (
  fieldName: AllowedFieldsForRemainingCharacters,
  combinedMaxLength?: boolean,
) => number {
  return (
    fieldName: AllowedFieldsForRemainingCharacters,
    combinedMaxLength = true,
  ) => {
    let value = field.value?.[fieldName].length || 0;
    if (combinedMaxLength) {
      ALLOWED_FIELDS_REMAINING_CHARACTERS.forEach((allowedField) => {
        if (allowedField === fieldName) return;
        value += field.value?.[allowedField].length || 0;
      });
    }
    const remaining = SUBJECT_AND_MESSAGE_MAX_LENGTH - value;
    return remaining < 0 ? 0 : remaining;
  };
}

export function getMaxLengthFactory<
  F extends
    | FormikSubscriberOutreachContentValue
    | FormikSubscriberOutreachClearMessageValue
>(
  field: FieldInputProps<F>,
): (fieldName: AllowedFieldsForRemainingCharacters) => number {
  const getRemainingCharactersFor = getRemainingCharactersFactory(field);
  return (fieldName: AllowedFieldsForRemainingCharacters) => {
    const remaining = getRemainingCharactersFor(fieldName);
    const current = field.value[fieldName]?.length || 0;
    return current + remaining;
  };
}
